/**
 * Ad Moderation — /admin/ad-moderation  (admin only)
 *
 * Phase 2, step 5: the review gate. Nothing serves until it passes here —
 * the serving selector (server/routes/ads.js) requires adv_creative.is_approved=1
 * and advertiser status in ('approved','active').
 *
 *  - Advertisers pending sign-up review → Approve (status 'active') / Suspend.
 *  - Creatives pending moderation → Approve (is_approved=1, 'approved') /
 *    Reject (is_approved=0, 'rejected' + reason, surfaced to the advertiser).
 *
 * Click-URL safety is flagged (https check); a full host allow-list is future work.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { FiCheckCircle, FiXCircle, FiFlag, FiExternalLink, FiAlertTriangle } from 'react-icons/fi';
import { loadTable, getTableData, saveRow } from '../db-viewer/data/dataAccess';
import { restoreSession } from '../../lib/session';
import './AdvertiserConsole.css';

type Row = Record<string, any>;
// eslint-disable-next-line eqeqeq
const eq = (a: any, b: any) => a == b;
const notDeleted = (r: Row) => !eq(r.is_deleted, 1);
const nowStr = () => new Date().toISOString().replace('T', ' ').substring(0, 19);
const isHttps = (u: string) => /^https:\/\//i.test(String(u || ''));
const AD_ORIGIN = process.env.REACT_APP_AD_ORIGIN || 'http://localhost:5000';

export const AdModerationPage: React.FC = () => {
  const session = restoreSession();
  const adminId = session ? parseInt(session.id, 10) : 0;

  const [loading, setLoading] = useState(true);
  const [advertisers, setAdvertisers] = useState<Row[]>([]);
  const [creatives, setCreatives] = useState<Row[]>([]);
  const [notice, setNotice] = useState('');
  const [rejecting, setRejecting] = useState<Row | null>(null);
  const [reason, setReason] = useState('');

  const reload = useCallback(async () => {
    await Promise.all([loadTable('adv_advertiser'), loadTable('adv_creative')]);
    setAdvertisers(getTableData('adv_advertiser').filter(notDeleted));
    setCreatives(getTableData('adv_creative').filter(notDeleted));
    setLoading(false);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const advName = (id: any) => advertisers.find(a => eq(a.advertiser_id, id))?.advertiser_name || `#${id}`;
  const flash = (m: string) => { setNotice(m); setTimeout(() => setNotice(''), 3000); };

  const pendingAdv = advertisers.filter(a => a.status === 'pending' && !eq(a.is_house, 1));
  const pendingCr = creatives.filter(c => c.moderation_status === 'pending');

  const approveAdv = async (a: Row) => {
    await saveRow('adv_advertiser', a.advertiser_id, { status: 'active', approved_by: adminId, approved_at: nowStr() }, 'advertiser_id');
    flash(`Approved advertiser "${a.advertiser_name}".`); reload();
  };
  const suspendAdv = async (a: Row) => {
    await saveRow('adv_advertiser', a.advertiser_id, { status: 'suspended' }, 'advertiser_id');
    flash(`Suspended advertiser "${a.advertiser_name}".`); reload();
  };
  const approveCr = async (c: Row) => {
    await saveRow('adv_creative', c.creative_id,
      { is_approved: 1, moderation_status: 'approved', reviewed_by: adminId, reviewed_at: nowStr(), rejection_reason: null }, 'creative_id');
    flash(`Approved creative "${c.creative_name}".`); reload();
  };
  const submitReject = async () => {
    if (!rejecting) return;
    await saveRow('adv_creative', rejecting.creative_id,
      { is_approved: 0, moderation_status: 'rejected', rejection_reason: reason.trim() || 'Rejected', reviewed_by: adminId, reviewed_at: nowStr() }, 'creative_id');
    setRejecting(null); setReason(''); flash('Creative rejected.'); reload();
  };

  if (loading) return <div className="adv-wrap"><p className="adv-muted">Loading moderation queue…</p></div>;

  return (
    <div className="adv-wrap">
      <header className="adv-header">
        <div>
          <h2><FiFlag /> Ad Moderation</h2>
          <p className="adv-muted">Review advertisers and creatives. Nothing serves until approved here.</p>
        </div>
      </header>
      {notice && <div className="adv-notice">{notice}</div>}

      {/* Advertisers awaiting approval */}
      <h3>Advertisers awaiting approval <span className="adv-muted">({pendingAdv.length})</span></h3>
      {pendingAdv.length === 0 ? <p className="adv-muted">No advertisers pending.</p> : (
        pendingAdv.map(a => (
          <div key={a.advertiser_id} className="adv-campaign-card">
            <div className="adv-row-between">
              <div>
                <strong>{a.advertiser_name}</strong>
                <div className="adv-muted adv-small">
                  {a.legal_name && <>{a.legal_name} · </>}{a.contact_email || 'no email'}
                  {a.website_url && <> · <a href={a.website_url} target="_blank" rel="noopener noreferrer">{a.website_url} <FiExternalLink size={11} /></a></>}
                </div>
              </div>
              <div className="adv-actions">
                <button className="adv-btn-primary" onClick={() => approveAdv(a)}><FiCheckCircle /> Approve</button>
                <button className="adv-btn-ghost" onClick={() => suspendAdv(a)}>Suspend</button>
              </div>
            </div>
          </div>
        ))
      )}

      {/* Creatives awaiting moderation */}
      <h3 style={{ marginTop: 24 }}>Creatives awaiting moderation <span className="adv-muted">({pendingCr.length})</span></h3>
      {pendingCr.length === 0 ? <p className="adv-muted">No creatives pending.</p> : (
        pendingCr.map(c => {
          const renderable = String(c.storage_path || '').startsWith('house:');
          const safeUrl = isHttps(c.click_url);
          return (
            <div key={c.creative_id} className="adv-campaign-card">
              <div className="adv-mod-row">
                <div className="adv-mod-preview">
                  {renderable
                    ? <img src={`${AD_ORIGIN}/ad/creative/${c.creative_id}.img?w=320&h=80`} alt={c.alt_text || c.creative_name} />
                    : <div className="adv-mod-noimg">Image pending upload</div>}
                </div>
                <div className="adv-mod-meta">
                  <strong>{c.creative_name}</strong> <span className="adv-muted adv-small">· {advName(c.advertiser_id)} · {c.width_px}×{c.height_px}</span>
                  <div className="adv-small" style={{ marginTop: 4 }}>
                    Click URL:{' '}
                    <span className={safeUrl ? 'adv-muted' : 'adv-url-bad'}>
                      {c.click_url || '(none)'} {!safeUrl && <><FiAlertTriangle size={11} /> not https</>}
                    </span>
                  </div>
                </div>
                <div className="adv-actions">
                  <button className="adv-btn-primary" onClick={() => approveCr(c)}><FiCheckCircle /> Approve</button>
                  <button className="adv-btn-ghost" onClick={() => { setRejecting(c); setReason(''); }}><FiXCircle /> Reject</button>
                </div>
              </div>
              {rejecting && eq(rejecting.creative_id, c.creative_id) && (
                <div className="adv-mod-reject">
                  <input autoFocus value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (shown to the advertiser)…" />
                  <button className="adv-btn-sm" onClick={submitReject}>Confirm reject</button>
                  <button className="adv-btn-sm" onClick={() => { setRejecting(null); setReason(''); }}>Cancel</button>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default AdModerationPage;
